/* eslint-disable sonarjs/cognitive-complexity */
import { WCardSuiteSummaryView, WCardVariationColorSummaryView } from '@zola/svc-web-api-ts-client';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import Tooltip from '@zola/zola-ui/src/components/Tooltip/Tooltip';
import { TagV2 } from '@zola/zola-ui/src/components/Tag';
import { CollaboratorTag } from '@zola/zola-ui/src/components/Tag/CollaboratorTag';
import { LetterpressTag } from '@zola/zola-ui/src/components/Tag/LetterpressTag';
import {
  LG_DESKTOP_MIN,
  MOBILE_MAX,
  TABLET_MAX,
} from '@zola-helpers/client/dist/es/util/responsive';
import svcImageLoader from 'libs/universal/nextImageLoaders/svcImageLoader';
import { excludeMobile } from '@zola/zola-ui/src/styles/emotion/mixins/responsive';

import React, { Fragment, useState } from 'react';
import Image from 'next/image';
import {
  CardImage,
  CardPreviewWrapper,
  Color,
  ColorWrapper,
  LinkWrapper,
  Name,
  NameWrapper,
  Price,
  ProductColorPicker,
  CardImageWrapper,
} from './CardPreview.styles';

const getImageUrl = (imageUuid: string) => {
  // resolve image host based on environment
  const env = (typeof window !== 'undefined' && window.zola && window.zola.env) || 'production';
  let hostname = '//images.zola.com';
  if (env === 'development' || env === 'staging') {
    hostname = '//stage-images.zola.com';
  }
  return `${hostname}/${imageUuid}`;
};

type CardPreviewProps = {
  card: WCardSuiteSummaryView;
  nextImage?: boolean;
};

const CardPreview: React.FC<CardPreviewProps> = (props): JSX.Element => {
  const { card, nextImage } = props;
  const { lead_card: leadCard, name, uuid, third_party: thirdParty } = card;

  const [activeColor, setActiveColor] = useState(leadCard?.colors ? leadCard.colors[0] : {});

  if (!leadCard) {
    return <Fragment />;
  }
  const { colors, min_price_cents: minPriceCents, type, letterpress, foil } = leadCard;

  // determine color variations to show
  const isExtraCustomizable = !!colors?.find(color => color.extra_customizable);
  const onlyColorAvailableIsEC = isExtraCustomizable && colors?.length === 1;

  const colorsToShow = isExtraCustomizable ? 2 : 3;
  const visibleColors = colors?.slice(0, colorsToShow);
  const hiddenOnMobileColors = colors?.slice(colorsToShow, colorsToShow + 3);
  const showSeeMoreLink = colors ? colors.length > 3 : false;

  // determine image to show
  const { plp_front_image_uuids: plpFrontImageUuids, plp_front_image_url } = activeColor;
  const imageUuid = plpFrontImageUuids?.square;
  const imageSrc = imageUuid ? getImageUrl(imageUuid) : plp_front_image_url;

  let priceString = '';
  if (minPriceCents) {
    priceString = minPriceCents < 100 ? `$0.${minPriceCents}` : `$${minPriceCents / 100}`;
  }

  // we are only supporting save the dates and invitations currently
  const urlSlug = type === 'SAVE_THE_DATE' ? 'save-the-date' : 'invitations';

  const pathName = `/wedding-planning/${urlSlug}/${uuid}`;

  const nameText = name ? name.split(' - ')[1] : '';

  const handleSelectColor = (colorObject: WCardVariationColorSummaryView) => {
    setActiveColor(colorObject);
  };

  const renderCardImage = () => {
    if (!imageSrc) return null;
    if (nextImage) {
      return (
        <CardImageWrapper>
          <Image
            loader={svcImageLoader}
            src={`https:${imageSrc}`}
            alt={`${name} preview`}
            sizes={`(max-width: ${MOBILE_MAX}px) 133px, (max-width: ${TABLET_MAX}) 182px, (max-width ${LG_DESKTOP_MIN}) 226px,  282px`}
            layout="responsive"
            height={226}
            width={226}
          />
        </CardImageWrapper>
      );
    }
    return <CardImage src={imageSrc} alt={`${name} preview`} />;
  };

  const renderColorSwatch = (color: WCardVariationColorSummaryView, notMobile = false) => {
    return (
      <ColorWrapper
        key={color.value}
        onClick={(): void => handleSelectColor(color)}
        active={color.value === activeColor.value}
        css={notMobile ? excludeMobile : undefined}
      >
        <Color
          data-label={color.label}
          style={{ background: `#${color.value}`, width: 16, height: 16 }}
        />
      </ColorWrapper>
    );
  };

  return (
    <CardPreviewWrapper>
      <LinkWrapper href={pathName}>
        {renderCardImage()}
        <NameWrapper>
          {nameText && <Name> {nameText}</Name>} {foil && <TagV2 />}
          {letterpress && <LetterpressTag />}
          {thirdParty && <CollaboratorTag text="Zola x Featured Artist" />}
        </NameWrapper>
      </LinkWrapper>
      {visibleColors && (
        <ProductColorPicker>
          {visibleColors.map(c => renderColorSwatch(c))}
          {hiddenOnMobileColors ? hiddenOnMobileColors.map(c => renderColorSwatch(c, true)) : null}
          {isExtraCustomizable && (
            <ColorWrapper active={onlyColorAvailableIsEC}>
              <Color style={{ width: 16, height: 16 }} rainbow />
              <Tooltip text="This design features extra customizable colors and fonts" />
            </ColorWrapper>
          )}
          {showSeeMoreLink && (
            <LinkV2 href={pathName} noUnderline>
              More
            </LinkV2>
          )}
        </ProductColorPicker>
      )}
      {priceString && <Price>From {priceString} each</Price>}
    </CardPreviewWrapper>
  );
};

export default CardPreview;
